import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  input,
  OnInit,
  output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { UpsDrawer } from '@ux/components/dist/types/components/drawer/ups-drawer';
import { distinctUntilChanged, filter, map, tap } from 'rxjs';
import { AuthenticationService } from 'src/app/core/auth/authentication.service';
import { UserRoles } from 'src/app/core/enums';
import { Screens } from 'src/app/core/enums/role-permissions.enum';
import { CurrentLocation } from 'src/app/core/models/admin/location-admin.model';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { UserService } from 'src/app/core/services/admin/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { ScreenPermissionsUtils } from 'src/app/core/utils/permissions/screen-permissions/screen-permissions.utils';
import { IMAGE_SRC } from '../shared/shared-constants';
import {
  CLOSED_SIDENAV_VALUES,
  LOGOUT_ICON,
  OPEN_SIDENAV_VALUES,
  SideNavType,
} from './side-nav.constants';

@Component({
  selector: 'app-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss'],
  standalone: false,
})
export class SideNavComponent implements OnInit, AfterViewInit {
  showBanner = input.required<boolean>();
  drawerToggle = output();

  readonly destroy = inject(DestroyRef);
  commonUtils = inject(CommonUtils);
  roleUtils = inject(ScreenPermissionsUtils);
  authService = inject(AuthenticationService);
  private route: Router = inject(Router);
  private userService = inject(UserService);
  private locationService = inject(LocationService);

  readonly LOGOUT = LOGOUT_ICON;
  readonly IMAGES = IMAGE_SRC;
  readonly SCREENS = Screens;
  readonly OPEN_VALUES = OPEN_SIDENAV_VALUES;
  readonly CLOSED_VALUES = CLOSED_SIDENAV_VALUES;
  readonly TYPE = SideNavType;

  allowToggle = false;
  sciIndicator = 0;
  userRole!: UserRoles;
  currentLocation!: CurrentLocation | undefined;

  @ViewChild('drawer') drawer?: ElementRef<UpsDrawer>;

  ngOnInit(): void {
    //On route change close the sidebar
    this.route.events
      .pipe(
        takeUntilDestroyed(this.destroy),
        filter((event) => event instanceof NavigationStart),
        map((event) => {
          if (event) {
            this.drawer?.nativeElement?.closeDrawer();
          }
        }),
      )
      .subscribe();

    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        map((user) => {
          this.sciIndicator = user.sciIndicator ?? 0;
          if (user?.roleName) {
            this.userRole = user.roleName;
          }
        }),
      )
      .subscribe();

    this.locationService
      .getCurrentLocation$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        tap((val) => {
          this.currentLocation = val;
        }),
      )
      .subscribe();

    this.authService.isDrawerOpen$
      .asObservable()
      .pipe(
        takeUntilDestroyed(this.destroy),
        distinctUntilChanged(),
        tap(() => {
          this.toggleDrawer();
        }),
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    if (this.drawer) {
      this.drawer.nativeElement.autoCloseOnMobile = false;
      this.toggleDrawer();
    }
  }

  toggleDrawer(): void {
    // check allow flag and toggle
    if (this.authService.allowDrawerToggle()) {
      this.drawer?.nativeElement.toggle();
    } else {
      this.drawer?.nativeElement.closeDrawer();
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.commonUtils.updateBanner(false);
  }
}
